// Render-side coat variety for the current derived stable (v2 palette).
//
// WHY THIS EXISTS: the wire-level appearance contract (horse-appearance.js,
// mirrored byte-for-byte by racecore/appearance.py and pinned by golden
// fixtures) selects uniformly from 20 realism-weighted presets, 13 of which
// are bay/chestnut/tan browns. Real thoroughbred demographics — but a field
// of eight reads as "a bunch of brown horses". This module upgrades the
// RENDERED coat to a weighted, game-legible equine palette while leaving the
// wire contract, the racecard, and the fixture-locked v1 derivation
// untouched. Racecards still carry v1 appearance; replays and live races
// both render through this same mapping, so the SAME horse shows the SAME
// coat everywhere, on every device.
//
// Contract:
//   - Input is the horse record as carried by the field/API (horseId, name,
//     appearance...). Identity is stableHorseIdentity — horseId first.
//   - A served coat OUTSIDE the legacy 20-preset set is an intentional
//     custom appearance (future persistent-horse services) and is respected
//     verbatim. Coats INSIDE the legacy set are placeholder-derived and are
//     remapped here.
//   - Deterministic: FNV-1a over a dedicated domain; no race RNG, no gate,
//     no field order, no Math.random. Assigned once at field build / UI
//     render — zero per-frame cost.
//   - Markings and model/profile variants pass through from v1 unchanged.
//   - Silks, badges, gate accents and every betting-identity surface are
//     untouched by design.

import {
  appearanceHash32,
  horseAppearance,
  stableHorseIdentity,
} from './horse-appearance.js';

export const COAT_PALETTE_VERSION = 2;
export const COAT_PALETTE_DOMAIN = 'horseracing-horse-coat:v2';

// The 20 v1 preset coats (horse-appearance.js COAT_PRESETS column 0,
// donor-inherited). Membership here marks a coat as "derived placeholder,
// safe to restyle". coatPalette.test.ts cross-checks this set against live
// v1 derivation output so it can never drift silently.
export const LEGACY_V1_COATS = Object.freeze(new Set([
  '#6b3826', '#542a22', '#422923', '#342622', '#242122',
  '#8a4328', '#a6532d', '#b76735', '#c09352', '#a7834f',
  '#8d744f', '#a66f4e', '#68645d', '#999892', '#737579',
  '#b1a69d', '#c8c4b9', '#94685c', '#686b70', '#56382e',
]));

// Weighted equine families. Weights are integer percent (sum 100) and are
// the variety dial: browns land at 40% so an 8-horse field averages ~5.8
// distinct families and an all-brown field is ~0.4^8 ≈ 0.07% (verified
// statistically in coatPalette.test.ts). Shades are [coat, mane, tail] in
// lowercase #rrggbb, chosen for separation at race distance: bays carry
// black points, chestnuts self/flaxen manes, palominos flaxen, buckskins
// black points — the classic silhouettes players can actually tell apart.
export const COAT_FAMILIES = Object.freeze([
  Object.freeze({
    id: 'bay', weight: 14,
    shades: Object.freeze([
      Object.freeze(['#7d4526', '#221a17', '#221a17']),
      Object.freeze(['#8d502c', '#241b18', '#241b18']),
      Object.freeze(['#6f3c22', '#1f1815', '#1f1815']),
    ]),
  }),
  Object.freeze({
    id: 'chestnut', weight: 13,
    shades: Object.freeze([
      Object.freeze(['#a54d24', '#7a3819', '#7a3819']),
      Object.freeze(['#b5581f', '#864017', '#864017']),
      Object.freeze(['#9c491f', '#e2c088', '#e2c088']),
    ]),
  }),
  Object.freeze({
    id: 'dark-bay', weight: 8,
    shades: Object.freeze([
      Object.freeze(['#4b2f21', '#191412', '#191412']),
      Object.freeze(['#553627', '#1c1614', '#1c1614']),
    ]),
  }),
  Object.freeze({
    id: 'black', weight: 10,
    shades: Object.freeze([
      Object.freeze(['#232126', '#141316', '#141316']),
      Object.freeze(['#2b2a30', '#17161a', '#17161a']),
    ]),
  }),
  Object.freeze({
    id: 'grey', weight: 12,
    shades: Object.freeze([
      Object.freeze(['#93979e', '#4e5158', '#4e5158']),
      Object.freeze(['#7e838c', '#43464d', '#43464d']),
      Object.freeze(['#a8adb4', '#5d6168', '#5d6168']),
    ]),
  }),
  Object.freeze({
    id: 'light-grey', weight: 6,
    shades: Object.freeze([
      Object.freeze(['#d3d5d9', '#8f939a', '#8f939a']),
      Object.freeze(['#c6c9ce', '#7d818a', '#7d818a']),
    ]),
  }),
  Object.freeze({
    id: 'white', weight: 4,
    shades: Object.freeze([
      Object.freeze(['#f0ede6', '#cfc9bd', '#cfc9bd']),
    ]),
  }),
  Object.freeze({
    id: 'palomino', weight: 8,
    shades: Object.freeze([
      Object.freeze(['#d9a854', '#f0e0ae', '#f0e0ae']),
      Object.freeze(['#c89a49', '#ecd9a4', '#ecd9a4']),
    ]),
  }),
  Object.freeze({
    id: 'buckskin', weight: 8,
    shades: Object.freeze([
      Object.freeze(['#c39457', '#26201b', '#26201b']),
      Object.freeze(['#b1854a', '#221d19', '#221d19']),
    ]),
  }),
  Object.freeze({
    id: 'red-roan', weight: 6,
    shades: Object.freeze([
      Object.freeze(['#b37f72', '#5f382f', '#5f382f']),
      Object.freeze(['#a5705f', '#54312a', '#54312a']),
    ]),
  }),
  Object.freeze({
    id: 'blue-roan', weight: 6,
    shades: Object.freeze([
      Object.freeze(['#6e7887', '#2e333d', '#2e333d']),
      Object.freeze(['#5f6a7a', '#282d37', '#282d37']),
    ]),
  }),
  Object.freeze({
    id: 'liver-chestnut', weight: 5,
    shades: Object.freeze([
      Object.freeze(['#6b3524', '#432018', '#432018']),
      Object.freeze(['#7a3f2a', '#4b251b', '#4b251b']),
    ]),
  }),
]);

// Families whose coats read as "brown" at race distance. Used by the
// variety test's all-brown-field bound; keep in sync with the palette.
export const BROWN_FAMILY_IDS = Object.freeze([
  'bay', 'chestnut', 'dark-bay', 'liver-chestnut',
]);

const TOTAL_WEIGHT = COAT_FAMILIES.reduce((sum, family) => sum + family.weight, 0);

function selector(identity, axis) {
  return appearanceHash32(`${COAT_PALETTE_DOMAIN}\0${identity}\0${axis}`);
}

/** Deterministic v2 coat for a stable identity: weighted family, then shade. */
export function deriveCoat(identity) {
  const familyRoll = selector(identity, 'family') % TOTAL_WEIGHT;
  let cursor = 0;
  let family = COAT_FAMILIES[COAT_FAMILIES.length - 1];
  for (const candidate of COAT_FAMILIES) {
    cursor += candidate.weight;
    if (familyRoll < cursor) {
      family = candidate;
      break;
    }
  }
  const shade = family.shades[selector(identity, 'shade') % family.shades.length];
  return {
    family: family.id,
    coat: shade[0],
    mane: shade[1],
    tail: shade[2],
  };
}

/**
 * The appearance every RENDER surface (3D body, tote profile, player
 * controls, React portraits, shell stable) should draw. Wire shape is the
 * canonical v1 object; legacy placeholder coats are upgraded to the v2
 * palette, intentional custom coats pass through untouched.
 */
export function renderHorseAppearance(horse = {}) {
  const wire = horseAppearance(horse);
  if (!LEGACY_V1_COATS.has(wire.coat)) return wire;
  const rendered = deriveCoat(stableHorseIdentity(horse));
  return {
    ...wire,
    coat: rendered.coat,
    mane: rendered.mane,
    tail: rendered.tail,
    coatFamily: rendered.family,
  };
}

/** Convenience for React surfaces holding `{horseId?, name?, appearance?}`. */
export function renderCoatColors(horse = {}) {
  const appearance = renderHorseAppearance(horse);
  return { coat: appearance.coat, mane: appearance.mane, tail: appearance.tail };
}
